const DISPOSITION_OPTIONS = [
  {
    value: "interested",
    label: "Interested",
    tone: "ready",
    helper: "Keeps the conversation open and can queue an interested follow-up task.",
  },
  {
    value: "not_interested",
    label: "Not interested",
    tone: "stale",
    helper: "Closes the conversation and marks the lead as dead.",
  },
  {
    value: "wrong_number",
    label: "Wrong number",
    tone: "stale",
    helper: "Closes the conversation and marks the number invalid.",
  },
  {
    value: "do_not_contact",
    label: "Do not contact",
    tone: "overdue",
    helper: "Closes the conversation and suppresses future outreach.",
  },
];

function currentQueueLabel(viewKey) {
  return (
    {
      mine: "My queue",
      unassigned: "Unassigned queue",
      overdue: "Overdue queue",
      open: "Open queue",
      focus: "Focus queue",
    }[viewKey || ""] || "CRM workspace"
  );
}

function taskImpact(mutation) {
  if (!mutation) {
    return "No session actions recorded for this conversation yet.";
  }
  const completed = Array.isArray(mutation.taskIdsCompleted) ? mutation.taskIdsCompleted.length : 0;
  const created = mutation.taskIdCreated ? 1 : 0;
  if (!completed && !created) {
    return "No follow-up task changes were reported.";
  }
  const parts = [];
  if (completed) {
    parts.push(`${completed} completed`);
  }
  if (created) {
    parts.push("1 created");
  }
  return `Task impact: ${parts.join(", ")}.`;
}

export function resolveConversationDetailActions(options = {}) {
  const settings = Object.assign(
    {
      role: "",
      currentUserId: null,
      conversation: null,
      activeView: null,
      focusQueue: null,
      canSelfAssign: false,
    },
    options
  );
  const conversation = settings.conversation || {};
  const privileged = settings.role === "tenant_owner" || settings.role === "manager";
  const effectiveView =
    settings.activeView === "focus"
      ? settings.focusQueue === "unassigned"
        ? "unassigned"
        : "mine"
      : settings.activeView;
  const assignedUserId = conversation.assigned_user_id || null;
  const assignedToCurrentUser = Boolean(assignedUserId && settings.currentUserId && assignedUserId === settings.currentUserId);
  const canWorkConversation = settings.role !== "viewer" && settings.role !== "";
  return {
    effectiveView,
    canReply: canWorkConversation,
    canDisposition: canWorkConversation,
    canClaim: !assignedUserId && effectiveView === "unassigned" && (settings.canSelfAssign || privileged),
    canUnassign: Boolean(assignedUserId) && (assignedToCurrentUser || privileged),
    canViewActivity: privileged,
  };
}

export function conversationDispositionOptions() {
  return DISPOSITION_OPTIONS.map((item) => ({ ...item }));
}

export function buildConversationWorkspaceSummary(options = {}) {
  const settings = Object.assign(
    {
      surfaceKey: "operator",
      effectiveView: null,
      conversation: null,
      messages: [],
      recentActions: [],
      latestMutation: null,
      activityUnavailableReason: null,
      visibleInWorkspace: true,
    },
    options
  );
  const conversation = settings.conversation || {};
  const messages = Array.isArray(settings.messages) ? settings.messages : [];
  const recentActions = Array.isArray(settings.recentActions) ? settings.recentActions : [];
  const latestMutation = settings.latestMutation || null;
  const queueLabel = currentQueueLabel(settings.effectiveView || settings.surfaceKey);
  const currentStatus = conversation.status || "open";
  return {
    summaryCards: [
      {
        label: "Queue visibility",
        value: settings.visibleInWorkspace ? "Visible now" : "Moved out",
        detail: settings.visibleInWorkspace
          ? `${queueLabel} still includes this conversation.`
          : `${queueLabel} no longer includes this conversation after the last update.`,
        tone: settings.visibleInWorkspace ? "ready" : "stale",
      },
      {
        label: "Conversation status",
        value: currentStatus,
        detail: conversation.assigned_user_id ? "Assigned to a teammate" : "Currently unassigned",
        tone: currentStatus === "open" ? "ready" : "stale",
      },
      {
        label: "Message count",
        value: String(messages.length),
        detail: messages.length ? "Thread loaded successfully." : "No thread messages were returned.",
        tone: messages.length ? "ready" : "muted",
      },
      {
        label: "Recent activity",
        value: recentActions.length ? `${recentActions.length} item(s)` : "No recent activity",
        detail: settings.activityUnavailableReason || "Queue action history for this conversation.",
        tone: recentActions.length ? "ready" : settings.activityUnavailableReason ? "muted" : "stale",
      },
    ],
    contextRows: [
      {
        label: "Current queue",
        value: queueLabel,
        tone: "ready",
        meta: settings.surfaceKey === "manager" ? "Manager workspace" : "Operator workspace",
      },
      {
        label: "Phone",
        value: conversation.phone_e164 || "phone unavailable",
        tone: "muted",
        meta: `Last activity ${conversation.last_message_at || conversation.created_at || "n/a"}`,
      },
      {
        label: "Aging",
        value: conversation.aging_bucket || "n/a",
        tone: conversation.aging_bucket || "muted",
        meta: conversation.first_response_overdue ? "First response overdue" : "First response on track",
      },
      {
        label: "Owner",
        value: conversation.assigned_user_id ? "assigned" : "unassigned",
        tone: conversation.assigned_user_id ? "ready" : "stale",
        meta: conversation.assigned_user_id || "No assigned user id present",
      },
    ],
    followUpRows: [
      {
        label: latestMutation ? "Latest session action" : "Disposition guidance",
        value: latestMutation
          ? latestMutation.summary || latestMutation.kind || "Updated in this session"
          : "Use disposition when the conversation outcome is clear.",
        tone: latestMutation ? latestMutation.tone || "ready" : "muted",
        meta: latestMutation ? taskImpact(latestMutation) : "Replying keeps the conversation live. Disposition closes or advances it.",
      },
      {
        label: "Next step",
        value:
          currentStatus === "closed"
            ? "Review filters or move to another record."
            : messages.length <= 1
              ? "Respond quickly to protect the first-response SLA."
              : "Use reply or disposition based on the latest message.",
        tone: currentStatus === "closed" ? "stale" : conversation.first_response_overdue ? "overdue" : "ready",
        meta: latestMutation && latestMutation.at ? `Updated ${latestMutation.at}` : "No session updates recorded yet.",
      },
    ],
    activityRows: recentActions.length
      ? recentActions.map((item) => ({
          label: item.summary || item.action_type || "Activity",
          value: item.source_kind || "manual",
          tone: item.source_kind === "automation" ? "stale" : "ready",
          meta: item.occurred_at || "timestamp unavailable",
        }))
      : [
          {
            label: "Activity timeline",
            value: settings.activityUnavailableReason ? "Unavailable here" : "No recent queue actions",
            tone: settings.activityUnavailableReason ? "muted" : "stale",
            meta: settings.activityUnavailableReason || "No manager-visible queue actions were returned for this conversation.",
          },
        ],
  };
}
